import React, { useContext, useState } from "react";
import { API } from "aws-amplify";
import moment from "moment";
import { useMutation } from "@apollo/client";
import {
  Comment,
  CommentActions,
  Dropdown,
  Form,
  Button,
  Label,
} from "semantic-ui-react";
import UserAvatar from "components/UserAvatar";
import UserContext from "contexts/UserContext";
import { ADD_REPORT_COMMENT } from "../GraphQL";
import { languageOptions, _t } from "../l10n";

const getTranslation = async (text, lang) => {
  const apiName = "KnowMyDayAPI";
  const path = "/translate";

  const attrs = {
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key": "c56zfumbvC2l5CtxV14bW5UHIf0MepJB2qyERnI5",
    },
    body: {
      text,
      lang,
    },
  };
  const result = await API.post(apiName, path, attrs);
  return result.translation;
};

const ReportComment = ({ comment, lastReadDate, showActions }) => {
  const [text, setText] = useState(comment.text);
  const [loading, setLoading] = useState(false);
  const [isTranslated, setIsTranslated] = useState(false);
  const appUser = useContext(UserContext);

  let unread = true;
  if (
    comment.user === appUser.id ||
    (lastReadDate && moment(lastReadDate).isAfter(moment(comment.date)))
  ) {
    unread = false;
  }

  return (
    <Comment>
      <Comment.Avatar
        src={<UserAvatar name={comment.commentUser.name} useSvg />}
      />
      <Comment.Content>
        <Comment.Author as="a">{comment.commentUser.name}</Comment.Author>
        <Comment.Metadata>
          <div>{moment(comment.date).fromNow()}</div>
        </Comment.Metadata>
        <Comment.Text>{text}</Comment.Text>
        {showActions ? (
          <CommentActions>
            {isTranslated ? (
              <Comment.Action
                onClick={() => {
                  setIsTranslated(false);
                  setText(comment.text);
                }}
              >
                {_t("show_original")}
              </Comment.Action>
            ) : (
              <Dropdown
                text={_t("see_translation")}
                options={languageOptions}
                loading={loading}
                onChange={async (_e, { value }) => {
                  setLoading(true);
                  setText(await getTranslation(comment.text, value));
                  setLoading(false);
                  setIsTranslated(true);
                }}
              />
            )}
          </CommentActions>
        ) : null}
      </Comment.Content>
      {unread ? (
        <Label color="teal" corner="right" icon="exclamation" size="mini" />
      ) : null}
    </Comment>
  );
};

const Comments = ({ report, student, teacherIsPro = false }) => {
  const appUser = useContext(UserContext);
  const { comments } = report;
  const [reply, setReply] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [addReportComment] = useMutation(ADD_REPORT_COMMENT);

  const handleReplyChange = (e, { value }) => {
    setReply(value);
  };

  const handleFormSubmit = async () => {
    setIsAdding(true);
    await addReportComment({
      variables: { report: report.id, student: student.id, comment: reply },
    });
    setIsAdding(false);
    setReply("");
  };

  return (
    <Comment.Group>
      {comments ? (
        comments.map((comment, i) => (
          <ReportComment
            showActions={teacherIsPro}
            key={`comment${i}`}
            comment={comment}
            lastReadDate={report.lastReadDate}
          />
        ))
      ) : (
        <small>No comments.</small>
      )}

      {(appUser.isTeacher || appUser.isParent) && teacherIsPro ? (
        <Form reply onSubmit={handleFormSubmit}>
          <Form.TextArea rows={2} value={reply} onChange={handleReplyChange} />
          <Button disabled={reply.length === 0 || isAdding}>Add Comment</Button>
        </Form>
      ) : null}
    </Comment.Group>
  );
};

export default Comments;
